import React, { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export type Language = "en" | "de" | "fr" | "es";

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
  flagCountry: "GB" | "DE" | "FR" | "ES";
}

export const LANGUAGES: LanguageOption[] = [
  { code: "en", name: "English", nativeName: "English", flagCountry: "GB" },
  { code: "de", name: "German", nativeName: "Deutsch", flagCountry: "DE" },
  { code: "fr", name: "French", nativeName: "Français", flagCountry: "FR" },
  { code: "es", name: "Spanish", nativeName: "Español", flagCountry: "ES" },
];

export const TRANSLATIONS: Record<Language, Record<string, string>> = {
  en: {
    "nav.home": "Home",
    "nav.about": "About",
    "nav.services": "Services",
    "nav.athletes": "Athletes",
    "nav.gallery": "Gallery",
    "nav.team": "Team",
    "nav.blog": "News",
    "nav.contact": "Contact",
    "nav.join": "Join Us",
    "nav.track": "Track Application",
    "nav.scouts": "Scouts & Clubs",
    "nav.apply_now": "Apply Now",
    "hero.tagline": "GLOBAL SPORTS MANAGEMENT & TALENT REPRESENTATION",
    "hero.title": "Elevating Elite Athletes. Shaping Champions.",
    "hero.desc": "We guide exceptional sports talent across Europe, the Americas, and Africa with strategic career pathways, elite club transfers, and holistic athlete branding.",
    "hero.cta_apply": "Join The Roster",
    "hero.cta_scouts": "Club & Scout Portal",
    "map.title": "Our Global Presence & Transfer Desks",
    "map.subtitle": "Connecting global talent with premier leagues across 5 strategic agency hubs.",
    "tracker.title": "Application Status Tracker",
    "tracker.subtitle": "Check the real-time review and scouting progress of your representation application.",
    "tracker.placeholder": "Enter Reference Code (e.g. ESA-2026-8942)",
    "tracker.btn": "Track Application",
    "scout.title": "Club & FIFA Scout VIP Fast-Track",
    "scout.subtitle": "Confidential inquiries for sporting directors, recruitment heads, and FIFA-licensed scouts.",
  },
  de: {
    "nav.home": "Startseite",
    "nav.about": "Über Uns",
    "nav.services": "Leistungen",
    "nav.athletes": "Athleten",
    "nav.gallery": "Galerie",
    "nav.team": "Team",
    "nav.blog": "Aktuelles",
    "nav.contact": "Kontakt",
    "nav.join": "Bewerben",
    "nav.track": "Bewerbungsstatus",
    "nav.scouts": "Scouts & Vereine",
    "nav.apply_now": "Jetzt Bewerben",
    "hero.tagline": "GLOBALES SPORTMANAGEMENT & TALENTVERTRETUNG",
    "hero.title": "Elite-Athleten fördern. Champions formen.",
    "hero.desc": "Wir begleiten herausragende Sporttalente in Europa, Amerika und Afrika mit strategischen Karrierewegen, Top-Transfers und ganzheitlichem Athleten-Branding.",
    "hero.cta_apply": "Kader beitreten",
    "hero.cta_scouts": "Vereins- & Scout-Portal",
    "map.title": "Unsere globale Präsenz & Transfer-Desks",
    "map.subtitle": "Verbindung von globalen Talenten mit europäischen und internationalen Spitzenligen über 5 Hauptstandorte.",
    "tracker.title": "Bewerbungs-Statusprüfung",
    "tracker.subtitle": "Prüfen Sie den aktuellen Prüfungs- und Scouting-Status Ihrer Bewerbung in Echtzeit.",
    "tracker.placeholder": "Referenznummer eingeben (z. B. ESA-2026-8942)",
    "tracker.btn": "Status abrufen",
    "scout.title": "Vereins- & FIFA-Scout VIP-Portal",
    "scout.subtitle": "Vertrauliche Transfer- und Probetraining-Anfragen für Sportdirektoren und lizensierte Scouts.",
  },
  fr: {
    "nav.home": "Accueil",
    "nav.about": "À Propos",
    "nav.services": "Services",
    "nav.athletes": "Athlètes",
    "nav.gallery": "Galerie",
    "nav.team": "Équipe",
    "nav.blog": "Actualités",
    "nav.contact": "Contact",
    "nav.join": "Rejoignez-nous",
    "nav.track": "Suivi Candidature",
    "nav.scouts": "Clubs & Recruteurs",
    "nav.apply_now": "Postuler",
    "hero.tagline": "GESTION SPORTIVE INTERNATIONALE & REPRÉSENTATION",
    "hero.title": "Élever les Talents d'Élite. Forger les Champions.",
    "hero.desc": "Nous guidons les meilleurs talents sportifs à travers l'Europe, les Amériques et l'Afrique grâce à des plans de carrière stratégiques et des transferts de premier plan.",
    "hero.cta_apply": "Rejoindre l'Agence",
    "hero.cta_scouts": "Portail Clubs & Recruteurs",
    "map.title": "Notre Présence Mondiale & Bureaux de Transfert",
    "map.subtitle": "Connecter les talents mondiaux aux plus grands championnats via nos 5 pôles stratégiques.",
    "tracker.title": "Suivi de Candidature",
    "tracker.subtitle": "Consultez en temps réel l'avancement de votre évaluation sportive.",
    "tracker.placeholder": "Entrez votre code de référence (ex: ESA-2026-8942)",
    "tracker.btn": "Vérifier le statut",
    "scout.title": "Portail VIP Clubs & Recruteurs FIFA",
    "scout.subtitle": "Demandes confidentielles pour directeurs sportifs et recruteurs certifiés.",
  },
  es: {
    "nav.home": "Inicio",
    "nav.about": "Nosotros",
    "nav.services": "Servicios",
    "nav.athletes": "Atletas",
    "nav.gallery": "Galería",
    "nav.team": "Equipo",
    "nav.blog": "Noticias",
    "nav.contact": "Contacto",
    "nav.join": "Únete",
    "nav.track": "Estado de Solicitud",
    "nav.scouts": "Clubes y Ojeadores",
    "nav.apply_now": "Aplicar Ahora",
    "hero.tagline": "GESTIÓN DEPORTIVA GLOBAL Y REPRESENTACIÓN DE TALENTO",
    "hero.title": "Elevando Atletas de Élite. Formando Campeones.",
    "hero.desc": "Guiamos a talentos deportivos excepcionales en Europa, América y África con trayectorias estratégicas y transferencias en ligas de primer nivel.",
    "hero.cta_apply": "Unirse al Roster",
    "hero.cta_scouts": "Portal de Clubes y Ojeadores",
    "map.title": "Nuestra Presencia Global y Oficinas de Transferencia",
    "map.subtitle": "Conectando talento internacional con las mejores ligas a través de 5 sedes estratégicas.",
    "tracker.title": "Seguimiento de Solicitud",
    "tracker.subtitle": "Consulta en tiempo real el progreso de evaluación y scouting de tu postulación.",
    "tracker.placeholder": "Ingresa tu código de referencia (ej: ESA-2026-8942)",
    "tracker.btn": "Consultar Estado",
    "scout.title": "Portal VIP para Clubes y Ojeadores FIFA",
    "scout.subtitle": "Consultas confidenciales de fichajes y pruebas para directores deportivos y agentes FIFA.",
  },
};

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  language: "en",
  setLanguage: () => {},
  t: (key, fallback) => fallback || key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLangState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem("esa_lang") as Language;
      if (saved && ["en", "de", "fr", "es"].includes(saved)) return saved;
    } catch {}
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLangState(lang);
    try {
      localStorage.setItem("esa_lang", lang);
    } catch {}
  };

  const t = (key: string, fallback?: string): string => {
    return TRANSLATIONS[language]?.[key] || TRANSLATIONS.en[key] || fallback || key;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
